import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/room-management.css";
import { getRoomsPaginated, patchRoom } from "../services/roomService";
import FormModal from "../components/ui/FormModal";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const RoomManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ room_code: "", name: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getRoomsPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setRooms(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load rooms");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  const filtered = useMemo(() => rooms || [], [rooms]);

  const allSelected = useMemo(
    () => filtered.length > 0 && selectedIds.size === filtered.length,
    [filtered, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.room_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const startEdit = (r) => {
    setEditingId(r.room_id);
    setEditForm({ room_code: r.room_code || "", name: r.name || "" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ room_code: "", name: "" });
  };
  const saveEdit = async () => {
    if (!editingId) return;
    const payload = {
      room_code: (editForm.room_code || "").trim(),
      name: (editForm.name || "").trim(),
    };
    if (!payload.room_code || !payload.name) return;
    try {
      const updated = await patchRoom(editingId, payload);
      setRooms((prev) =>
        prev.map((r) => {
          if (r.room_id !== editingId) return r;
          return {
            ...r,
            ...updated,
            floor_id: updated.floor_id ?? r.floor_id,
            floor_name: updated.floor_name || r.floor_name,
          };
        }),
      );
      toast.success(`Room "${payload.name}" has been updated successfully!`);
      cancelEdit();
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to update room. Please try again.");
      cancelEdit();
      await reload();
    }
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container room-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Rooms</h2>
            <p className="page-subtitle">
              View and edit room code and name. No create or delete.
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by code, name, or floor"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "#eff6ff",
              borderRadius: "0.75rem",
              border: "1px solid #bfdbfe",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "#1e40af", fontSize: "0.9375rem" }}>
                {selectedIds.size} {selectedIds.size === 1 ? "room" : "rooms"} selected
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="room-pagination-top">
          <label htmlFor="room-limit" className="room-pagination-label">
            Rows per page:
          </label>
          <select
            id="room-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
            disabled={loading}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {loading ?
          <div className="no-results">Loading rooms...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Room Code</div>
              <div className="cell">Room Name</div>
              <div className="cell">Floor</div>
              <div className="cell actions">Actions</div>
            </div>

            {(filtered || []).length === 0 ?
              <div className="no-results">No rooms found</div>
            : filtered.map((r) => (
                <div
                  key={r.room_id}
                  className={`roles-table-row ${selectedIds.has(r.room_id) ? "selected" : ""}`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.room_id)}
                      onChange={() => toggleSelect(r.room_id)}
                    />
                  </div>
                  <div className="cell">{r.room_code || "-"}</div>
                  <div className="cell">{r.name || "-"}</div>
                  <div className="cell">{r.floor_name || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === r.room_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === r.room_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(r.room_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === r.room_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              startEdit(r);
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="room-pagination-bottom">
          <div className="room-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="room-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={loading || currentPage <= 1}
            >
              Previous
            </button>
            {visiblePageItems.map((item, idx) =>
              item === "ellipsis" ?
                <span key={`ellipsis-${idx}`} style={{ padding: "0 6px", color: "#6b7280" }}>
                  …
                </span>
              : <button
                  key={item}
                  className={`${currentPage === item ? "primary-btn" : "secondary-btn"} sm`}
                  onClick={() => setPage(Number(item))}
                  disabled={loading}
                >
                  {item}
                </button>,
            )}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={loading || currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>

        <FormModal
          open={editingId != null}
          title="Edit Room"
          onCancel={cancelEdit}
          footer={
            <>
              <button className="secondary-btn" onClick={cancelEdit}>
                Cancel
              </button>
              <button className="primary-btn" onClick={saveEdit}>
                Save
              </button>
            </>
          }
          size="compact"
        >
          <div style={{ display: "grid", gap: 12 }}>
            <div className="field" style={{ margin: 0 }}>
              <label>Room Code</label>
              <input
                value={editForm.room_code}
                onChange={(e) =>
                  setEditForm((s) => ({
                    ...s,
                    room_code: e.target.value,
                  }))
                }
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>Room Name</label>
              <input
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((s) => ({ ...s, name: e.target.value }))
                }
              />
            </div>
          </div>
        </FormModal>
      </div>
    </div>
  );
};
export default RoomManagementPage;

